import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetContactListsQuery, useDeleteContactListMutation } from "@/store/rest_api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, MoreVertical } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import SkipTraceButton from "@/components/skip-trace/SkipTraceButton";
import { SkipTraceScope } from "@/enum/SkipTraceScope";

export default function ContactListsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const { data, isLoading } = useGetContactListsQuery({ page, size: 10 });
  const [deleteList] = useDeleteContactListMutation();

  const lists = data?.items ?? [];

  const handleDelete = async (id: string) => {
    await deleteList(id);
    toast.success("List deleted");
  };

  if (isLoading) return <div className="p-6">Loading...</div>;

  return (
      <div className="p-4 space-y-6">
        <div className="flex justify-between">
          <h1 className="text-3xl font-bold">Contact Lists</h1>
          <Button onClick={() => navigate("/contacts/lists/new")}>
            <Plus className="mr-2 h-4 w-4" />
            Create List
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{lists.length} Lists</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {lists.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell>{l.name}</TableCell>
                      <TableCell>{l.description || "—"}</TableCell>
                      <TableCell>
                        {l.createdAt ? new Date(l.createdAt).toLocaleDateString() : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="mr-1 inline-flex align-middle">
                          <SkipTraceButton scope={SkipTraceScope.CONTACT_LIST} targetId={l.id} targetName={l.name} iconOnly />
                        </span>
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/contacts/lists/${l.id}/edit`)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="ghost" title="Options">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(l.id)}>
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete list
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="flex justify-between mt-4">
              <Button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                Prev
              </Button>
              <Button disabled={data?.last} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
  );
}